import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './CountingScales.css';
import { useProducts, type CountingScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const CountingScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<CountingScalesSpecs>('counting-scales', editMode);
  const navigate = useNavigate();

  const optionalFeatures = [
    'Diferentes tamaños de plataforma',
    'Cubiertas de protección contra aire',
    'Opciones de batería externa',
    'Plataformas remotas',
    'Teclados numéricos',
    'Software de reportes'
  ];

  const applications = [
    {
      title: 'Control de Inventarios',
      description: 'Seguimiento preciso de entradas y salidas en almacenes y centros de distribución.',
    },
    {
      title: 'Manufactura',
      description: 'Conteo de componentes y piezas en líneas de producción y ensamble.',
    },
    {
      title: 'Farmacéutica',
      description: 'Conteo preciso de tabletas, cápsulas y productos farmacéuticos.',
    },
    {
      title: 'Electrónica',
      description: 'Conteo de componentes electrónicos, resistencias, capacitores y semiconductores.',
    },
  ];

  return (
    <div className="csc-page">
      {error && <div className="csc-status-bar csc-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="csc-hero">
        <div className="container csc-hero-content">
          <nav className="csc-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="csc-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="csc-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas Contadoras</span>
          </nav>
          <h1 className="csc-hero-title">Básculas Contadoras</h1>
          <p className="csc-hero-subtitle">
            Soluciones precisas para conteo de inventarios y control de piezas
            en aplicaciones industriales.
          </p>
          {authType === 'internal' && (
            <button
              className={`csc-btn-edit-mode${editMode ? ' csc-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="csc-hero-image" aria-hidden="true">
          <img src="/images/counting-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="csc-overview">
        <div className="container">
          <div className="csc-overview-grid">
            <div className="csc-overview-text">
              <span className="csc-label">Descripción general</span>
              <h2>Precisión y confiabilidad en cada conteo</h2>
              <p>
                Representamos Avery Weigh-Tronix y otras marcas líderes en tecnología
                de pesaje y conteo de alta precisión. Nuestras básculas contadoras
                incorporan tecnología Digital Quartzell para ofrecer resultados exactos
                en su proceso de inventario, manufactura o control de calidad.
              </p>
            </div>
            <div className="csc-overview-image">

            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="csc-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/counting-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.model ?? undefined}
                  sub={model.specs?.resolution ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.connectivity?.length ? [{ icon: '🔌', label: 'Conectividad',    items: (model.specs?.connectivity  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.features?.length     ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(model.specs?.capacities?.length   ? [{ icon: '📦', label: 'Capacidades',     items: (model.specs?.capacities  ?? []).filter((x): x is string => x != null)}] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Optional features ────────────────────────────────── */}
      <section className="csc-options">
        <div className="container">
          <div className="csc-options-inner">
            <div className="csc-options-header">
              <span className="csc-label">Personalización</span>
              <h2 className="csc-section-title">Características Opcionales</h2>
              <p>
                Adapte su báscula contadora a los requerimientos específicos
                de su proceso productivo.
              </p>
            </div>
            <ul className="csc-options-list" aria-label="Características opcionales">
              {optionalFeatures.map((feature, index) => (
                <li key={index} className="csc-option-item">
                  <svg className="csc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="csc-applications">
        <div className="container">
          <span className="csc-label csc-label--light">Industrias</span>
          <h2 className="csc-section-title csc-section-title--light">Aplicaciones</h2>
          <div className="csc-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="csc-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Brands ───────────────────────────────────────────── */}
      <section className="csc-brands">
        <div className="container">
          <span className="csc-label">Tecnología</span>
          <h2 className="csc-section-title">Marcas y Tecnología</h2>
          <div className="csc-brands-grid">
            <div className="csc-brand-card">
              <h3>Avery Weigh-Tronix</h3>
              <p>
                Representamos Avery Weigh-Tronix, líder mundial en tecnología de pesaje
                y conteo, reconocida por su precisión y confiabilidad en aplicaciones industriales.
              </p>
            </div>
            <div className="csc-brand-card">
              <h3>Tecnología Quartzell</h3>
              <p>
                Nuestras básculas contadoras utilizan tecnología Digital Quartzell,
                que proporciona la máxima precisión y estabilidad en el conteo de piezas.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CountingScales;
